import {
  parseApiErrorFromResponse,
  toProviderIssueError,
} from "@/lib/api-errors";
import { env } from "@/env";
import z from "zod/v4";

export const ApplicationConfigResponseSchema = z.object({
  enabled: z.boolean(),
});

export type ApplicationConfigResponse = z.infer<
  typeof ApplicationConfigResponseSchema
>;

export const FxDestinationBeneficiarySchema = z.object({
  id: z.string(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  accountName: z.string(),
  accountNumber: z.string(),
  nationalId: z.string(),
  bankName: z.string(),
  payoutRail: z.enum(["local", "swift"]).nullable().optional(),
});
export type FxDestinationBeneficiary = z.infer<
  typeof FxDestinationBeneficiarySchema
>;

export const FxEligibilityReasonSchema = z.enum([
  "NO_DESTINATION_WALLET",
  "MULTIPLE_DESTINATION_WALLETS",
  "NO_ACTIVE_BENEFICIARY",
]);
export type FxEligibilityReason = z.infer<typeof FxEligibilityReasonSchema>;

export const SourceWalletEligibilityReasonSchema = z.enum([
  "NO_ELIGIBLE_DESTINATION_CORRIDOR",
]);
export type SourceWalletEligibilityReason = z.infer<
  typeof SourceWalletEligibilityReasonSchema
>;

const fxCurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);

export const FxEligibilityResponseSchema = z.object({
  applicationId: z.string(),
  provider: z.enum(["verto-fx", "mocked"]),
  sourceCurrencies: z.array(fxCurrencyCodeSchema),
  destinationCurrencies: z.array(fxCurrencyCodeSchema),
  /** All provider-supported corridors (for rates); independent of eligible trading subset. */
  supportedCurrencyPairs: z
    .array(
      z.object({
        source: fxCurrencyCodeSchema,
        destination: fxCurrencyCodeSchema,
      }),
    )
    .default([]),
  sourceWalletOptions: z.array(
    z.object({
      internalWalletId: z.string(),
      providerWalletId: z.number(),
      /** @deprecated Use internalWalletId / providerWalletId. */
      walletId: z.string().optional(),
      type: z.enum(["standard", "fx_balance"]),
      currency: z.string().regex(/^[A-Z]{3}$/),
      fundingMode: z.enum(["AUTO_PULL", "PRE_FUNDED"]),
      availableBalance: z.number(),
      displayLabel: z.string(),
      eligible: z.boolean(),
      reasons: z.array(SourceWalletEligibilityReasonSchema),
    }),
  ),
  destinationAvailability: z.array(
    z.object({
      currency: z.string().regex(/^[A-Z]{3}$/),
      eligible: z.boolean(),
      reasons: z.array(FxEligibilityReasonSchema),
      destinationWalletId: z.string().nullable().optional(),
    }),
  ),
  destinationBeneficiaries: z.array(FxDestinationBeneficiarySchema).default([]),
});
export type FxEligibilityResponse = z.infer<typeof FxEligibilityResponseSchema>;

export class ApplicationConfigNotFoundError extends Error {
  constructor() {
    super("Application not found");
    this.name = "ApplicationConfigNotFoundError";
  }
}

export const applicationApi = {
  // Get application configuration (user-facing: enabled only)
  getApplicationConfig: async (
    applicationId: string,
    accessToken: string,
  ): Promise<ApplicationConfigResponse> => {
    const response = await fetch(
      `${env.VITE_MERCHANT_PORTAL_BACKEND_URL}/api/application/${applicationId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-application-id": applicationId,
        },
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        // Backward compatibility for older backend behavior:
        // missing config used to return 404, now it returns 200 { enabled: false }.
        return { enabled: false };
      }
      const parsed = await parseApiErrorFromResponse(response);
      throw new Error(parsed.message || "Failed to fetch application config");
    }

    const json = await response.json();
    return ApplicationConfigResponseSchema.parse(json);
  },

  getFxEligibility: async (
    applicationId: string,
    accessToken: string,
  ): Promise<FxEligibilityResponse> => {
    const response = await fetch(
      `${env.VITE_MERCHANT_PORTAL_BACKEND_URL}/api/application/${applicationId}/fx-eligibility`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-application-id": applicationId,
        },
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new ApplicationConfigNotFoundError();
      }
      const parsed = await parseApiErrorFromResponse(response);
      const providerIssueError = toProviderIssueError(parsed);
      if (providerIssueError) {
        throw providerIssueError;
      }
      throw new Error(parsed.message || "Failed to fetch FX eligibility");
    }

    const json = await response.json();
    return FxEligibilityResponseSchema.parse(json);
  },
};
